/**
 * "My stickers" on the web — stickers the user made (sticker maker) or saved
 * from a chat. Kept sealed at rest under the vault key in IndexedDB
 * (@veto/storage saveMySticker), like the phone keeps them encrypted.
 * Older builds stored imports as plain data URLs in localStorage: those are
 * migrated once (oldest first, order kept) and then removed from localStorage.
 */
import {
  MY_STICKER_MAX_BYTES,
  deleteMySticker,
  getAtRestCipher,
  listMyStickers,
  saveMySticker,
  type MySticker,
} from "@veto/storage";
import {
  clearLegacyCustomStickers,
  hasLegacyCustomStickers,
  loadCustomStickers,
} from "../components/mediaRecents";
import { showAppToast } from "../appToast";
import { sniffStickerType, stickerBlobFromImageUrl, stickerExtension } from "./stickerImage";

export type { MySticker } from "@veto/storage";

type Cipher = NonNullable<ReturnType<typeof getAtRestCipher>>;

/** Decrypted list, valid only for the cipher (vault unlock) it was read with. */
let cache: { cipher: Cipher; list: MySticker[] } | null = null;
let migration: Promise<void> | null = null;
const listeners = new Set<() => void>();

function changed(): void {
  cache = null;
  for (const fn of listeners) fn();
}

/** Re-render hooks when stickers are added / edited / deleted anywhere. */
export function onMyStickersChanged(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

function dataUrlBytes(dataUrl: string): Uint8Array | null {
  const comma = dataUrl.indexOf(",");
  if (comma < 0 || !/;base64$/i.test(dataUrl.slice(0, comma))) return null;
  try {
    const bin = atob(dataUrl.slice(comma + 1));
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  } catch {
    return null;
  }
}

function sameBytes(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

/**
 * One-time move of legacy localStorage stickers into the sealed store. Needs
 * the vault unlocked; if anything fails the localStorage copy is kept and the
 * migration runs again next time (already-moved stickers are not duplicated).
 */
export function migrateLegacyStickers(): Promise<void> {
  if (migration) return migration;
  const run = migrateOnce()
    .catch(() => false)
    .then((finished) => {
      if (!finished) migration = null; // locked or failed: try again next time
    });
  migration = run;
  return run;
}

/** True when there is nothing (left) to migrate. */
async function migrateOnce(): Promise<boolean> {
  if (!hasLegacyCustomStickers()) return true;
  if (!getAtRestCipher()) return false; // needs the vault key to seal
  const legacy = loadCustomStickers(); // newest first
  const existing = await listMyStickers();
  const base = Date.now() - legacy.length - 1;
  let failed = false;
  for (let k = legacy.length - 1; k >= 0; k--) {
    const bytes = dataUrlBytes(legacy[k]!.dataUrl);
    const type = bytes ? sniffStickerType(bytes) : null;
    if (!bytes || !type || bytes.length > MY_STICKER_MAX_BYTES) continue; // unusable entry: dropped
    if (existing.some((s) => sameBytes(s.bytes, bytes))) continue;
    try {
      await saveMySticker({ bytes, mediaType: type, updatedAt: base + (legacy.length - 1 - k) });
    } catch {
      failed = true;
    }
  }
  if (failed) return false;
  clearLegacyCustomStickers();
  changed();
  return true;
}

/** Newest first. Empty while the vault is locked. */
export async function loadMyStickers(): Promise<MySticker[]> {
  await migrateLegacyStickers();
  const cipher = getAtRestCipher();
  if (!cipher) return [];
  if (cache && cache.cipher === cipher) return cache.list;
  const list = await listMyStickers();
  cache = { cipher, list };
  return list;
}

/** Add (or replace after editing) a sticker picture. Throws a user-facing message. */
export async function addMySticker(blob: Blob, opts: { replaceId?: string } = {}): Promise<MySticker> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  const type = sniffStickerType(bytes);
  if (!type) throw new Error("Stickers must be PNG, WebP, GIF or JPEG pictures.");
  const saved = await saveMySticker({ bytes, mediaType: type, replaceId: opts.replaceId });
  changed();
  return saved;
}

export async function removeMySticker(id: string): Promise<void> {
  await deleteMySticker(id);
  changed();
}

/** A My sticker as a File for the existing E2EE sticker attachment path (CT 9, kind "sticker"). */
export function myStickerFile(s: MySticker): File {
  return new File([s.bytes as BlobPart], `sticker-${Date.now()}.${stickerExtension(s.mediaType)}`, {
    type: s.mediaType,
  });
}

/**
 * "Save to My stickers" on a sent or received photo sticker. `url` is the
 * bubble's decrypted blob: URL; the picture is re-encoded on the device.
 */
export async function saveStickerFromUrl(url: string): Promise<void> {
  try {
    const blob = await stickerBlobFromImageUrl(url);
    await addMySticker(blob);
    showAppToast("Saved to My stickers");
  } catch (e) {
    showAppToast(e instanceof Error && e.message ? e.message : "Couldn't save that sticker.");
  }
}
